import type { Theme } from '@/store/activity-store'

/**
 * Base map style: CARTO's free "Positron" (light) / "Dark Matter" (dark)
 * basemaps (https://carto.com/basemaps), requested directly from CARTO's
 * CDN. Chosen because it's free, requires no API key, and ships both a
 * light and a dark variant so the map can match the app's theme (plain
 * OpenStreetMap tiles or MapLibre's official demo style only have one
 * visual variant). This is the one disclosed third-party network
 * exception documented in `SECURITY.md`: map tile requests leave the
 * browser and go to CARTO's CDN, never activity data.
 *
 * Returns CARTO's own published style URL rather than a hand-built
 * `StyleSpecification` — MapLibre's `style` option accepts a URL string
 * and fetches/parses it itself (`StyleSpecification | string`). This was
 * switched from a hand-built raster style pointing at
 * `{s}.basemaps.cartocdn.com/{light,dark}_all/{z}/{x}/{y}{r}.png` after
 * live testing found that raw raster XYZ endpoint now serves a
 * watermarked "API KEY REQUIRED" placeholder instead of real tiles —
 * CARTO's current genuinely-free, unauthenticated offering is this
 * published vector style instead (verified directly: both style URLs
 * below return 200 with a real MapLibre style referencing
 * `tiles.basemaps.cartocdn.com/.../vectortiles/carto.streets/v2/{z}/{x}/{y}.mvt`,
 * and a sample `.mvt` tile fetch returns real vector tile data, not a
 * placeholder).
 */
export function buildMapStyle(theme: Theme): string {
  const variant = theme === 'dark' ? 'dark-matter-gl-style' : 'positron-gl-style'
  return `https://basemaps.cartocdn.com/gl/${variant}/style.json`
}
